import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { v3Api } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import DateRangeControl, { computePreset } from '../AnalyticsPro/DateRangeControl';
import SeriesRow from './Comparison/SeriesRow';
import MultiSeriesChart from './Comparison/MultiSeriesChart';
import ResultsKpiStrip from './Comparison/ResultsKpiStrip';
import ResultsTable from './Comparison/ResultsTable';
import ExportButton from './Comparison/ExportButton';

/**
 * Compare Anything — Multi-series upgrade.
 *
 * Up to 6 simultaneous series. Each series:
 *   - any entity type (Tenant / Category / Subcategory / Property /
 *     Floor / Group / Zone / Vertical)
 *   - searchable name picker (no raw IDs)
 *   - optional per-series date-range override (Last year, 2 years ago,
 *     custom from/to) → enables year-over-year overlay via the
 *     "Align periods" toggle in the chart.
 *
 * Global controls: metric, granularity, default date range, chart type,
 * normalize mode, align-periods toggle.
 *
 * Shareable URLs supported via ?series=base64(json) (or legacy
 * ?entityAType/entityAId/entityBType/entityBId for back-compat).
 */

const MAX_SERIES = 6;
const PALETTE = ['#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899'];
const EMPTY_ROW = () => ({ type: 'Tenant', id: '', label: '', dateRange: null });

export default function ComparisonTool() {
    const { t } = useTranslation();
    const { concessionaireId } = useAuth();
    const [searchParams] = useSearchParams();

    const initial = useMemo(() => parseInitialState(searchParams), [searchParams]);

    const [seriesEntries, setSeriesEntries] = useState(initial.series);
    const [metric, setMetric] = useState(initial.metric);
    const [granularity, setGranularity] = useState(initial.granularity);
    const [dateRange, setDateRange] = useState(initial.dateRange);
    const [chartType, setChartType] = useState('line');     // line | area | bar | stacked | stacked100
    const [normalize, setNormalize] = useState('absolute'); // absolute | index100 | percent
    const [alignPeriods, setAlignPeriods] = useState(false);

    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const allReady = seriesEntries.length > 0 && seriesEntries.every(s => s.id);
    const groupBy = granularity === 'monthly' ? 'month' : granularity === 'yearly' ? 'year' : 'date';

    const updateSeries = (i, next) => {
        setSeriesEntries(prev => prev.map((s, idx) => (idx === i ? next : s)));
    };
    const removeSeries = (i) => {
        setSeriesEntries(prev => prev.filter((_, idx) => idx !== i));
    };
    const addSeries = () => {
        if (seriesEntries.length >= MAX_SERIES) return;
        setSeriesEntries(prev => [...prev, EMPTY_ROW()]);
    };

    const runCompare = async () => {
        setLoading(true); setError(null);
        try {
            const payload = {
                series: seriesEntries.map(s => ({
                    type: s.type,
                    id: s.id,
                    label: s.label,
                    dateRange: s.dateRange || undefined,
                    concessionaireId,
                })),
                metric,
                dateRange,
                groupBy: [groupBy],
            };
            const res = await v3Api.comparison.compare(payload);
            // eslint-disable-next-line no-console
            console.log('[ComparisonTool] compare response:', res?.data?.series?.length, 'series');
            setResult(res);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    // Push shareable URL when result lands. Use window.history directly so
    // location.key does NOT change — the global AppLayout CSSTransition is
    // keyed on location.key and would unmount this page (losing result state)
    // if React Router's setSearchParams updated it.
    React.useEffect(() => {
        if (!result) return;
        const sp = new URLSearchParams();
        sp.set('series', encodeStateParam(seriesEntries));
        sp.set('metric', metric);
        sp.set('granularity', granularity);
        sp.set('from', dateRange.from);
        sp.set('to', dateRange.to);
        const next = `${window.location.pathname}?${sp.toString()}`;
        window.history.replaceState(null, '', next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    const seriesData = result?.data?.series || [];
    // eslint-disable-next-line no-console
    console.log('[ComparisonTool] render — result=', result ? `${seriesData.length} series` : 'null');

    return (
        <div className="v3" style={{ padding: '16px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <h1 className="v3-heading">{t('Compare Anything')}</h1>
                <div className="v3-text-muted" style={{ fontSize: 12 }}>
                    {t('Up to {{count}} series · any entity · any year · any vertical', { count: MAX_SERIES })}
                </div>
            </div>

            {/* Series builder */}
            <div className="v3-card" style={{ padding: 18, marginTop: 16 }}>
                <div className="v3-label" style={{ marginBottom: 8 }}>{t('Series')}</div>
                {seriesEntries.map((s, i) => (
                    <SeriesRow
                        key={i}
                        index={i}
                        entry={s}
                        color={PALETTE[i]}
                        globalRange={dateRange}
                        onChange={(next) => updateSeries(i, next)}
                        onRemove={() => removeSeries(i)}
                        removable={seriesEntries.length > 1}
                    />
                ))}
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        type="button"
                        onClick={addSeries}
                        disabled={seriesEntries.length >= MAX_SERIES}
                        className="v3-btn"
                        style={{ fontSize: 12 }}
                    >
                        {t('+ Add comparison')} ({seriesEntries.length}/{MAX_SERIES})
                    </button>
                </div>
            </div>

            {/* Global controls */}
            <div className="v3-card" style={{ padding: 18, marginTop: 16 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
                    <Field label={t('Metric')}>
                        <select value={metric} onChange={e => setMetric(e.target.value)} style={selectStyle}>
                            <option value="sales">{t('Sales')}</option>
                            <option value="revenue">{t('Revenue Share')}</option>
                            <option value="transactions">{t('Transactions')}</option>
                        </select>
                    </Field>
                    <Field label={t('Granularity')}>
                        <select value={granularity} onChange={e => setGranularity(e.target.value)} style={selectStyle}>
                            <option value="daily">{t('Daily')}</option>
                            <option value="monthly">{t('Monthly')}</option>
                            <option value="yearly">{t('Yearly')}</option>
                        </select>
                    </Field>
                    <div style={{ flex: '1 1 320px' }}>
                        <div className="v3-label">{t('Default date range')}</div>
                        <div style={{ marginTop: 4 }}>
                            <DateRangeControl range={dateRange} onChange={setDateRange} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', flex: 'none' }}>
                        <button
                            onClick={runCompare}
                            disabled={loading || !allReady}
                            className="v3-btn v3-btn-primary"
                        >
                            {loading ? t('Loading…') : t('Compare')}
                        </button>
                    </div>
                </div>
            </div>

            {error && <div className="v3-body" style={{ color: 'var(--v3-red)', padding: 16 }}>{error}</div>}

            {/* Results */}
            {result && (
                <>
                    <ResultsKpiStrip seriesData={seriesData} metric={metric} palette={PALETTE} />

                    <div className="v3-card" style={{ marginTop: 16, padding: 18 }}>
                        <div style={controlsRow}>
                            <ToggleGroup
                                label={t('Chart')}
                                value={chartType}
                                options={[
                                    { value: 'line', label: 'Line' },
                                    { value: 'area', label: 'Area' },
                                    { value: 'bar', label: 'Bar' },
                                    { value: 'stacked', label: 'Stacked' },
                                    { value: 'stacked100', label: '100%' },
                                ]}
                                onChange={setChartType}
                            />
                            <ToggleGroup
                                label={t('Normalize')}
                                value={normalize}
                                options={[
                                    { value: 'absolute', label: 'Absolute' },
                                    { value: 'index100', label: 'Index 100' },
                                    { value: 'percent', label: '% share' },
                                ]}
                                onChange={setNormalize}
                            />
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--v3-text-muted)' }}>
                                <input
                                    type="checkbox"
                                    checked={alignPeriods}
                                    onChange={e => setAlignPeriods(e.target.checked)}
                                />
                                {t('Align periods (overlay year-over-year)')}
                            </label>
                            <div style={{ marginLeft: 'auto' }}>
                                <ExportButton seriesData={seriesData} metric={metric} alignPeriods={alignPeriods} />
                            </div>
                        </div>
                        <MultiSeriesChart
                            seriesData={seriesData}
                            metric={metric}
                            chartType={chartType}
                            normalize={normalize}
                            alignPeriods={alignPeriods}
                            palette={PALETTE}
                        />
                    </div>

                    <div className="v3-card" style={{ marginTop: 16, padding: 18 }}>
                        <div className="v3-label" style={{ marginBottom: 8 }}>{t('Data')}</div>
                        <ResultsTable seriesData={seriesData} metric={metric} alignPeriods={alignPeriods} palette={PALETTE} />
                    </div>
                </>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="v3-label">{label}</div>
            <div style={{ marginTop: 4 }}>{children}</div>
        </div>
    );
}

function ToggleGroup({ label, value, options, onChange }) {
    const { t } = useTranslation();
    return (
        <div>
            <div className="v3-label" style={{ fontSize: 11 }}>{label}</div>
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                {options.map(o => (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => onChange(o.value)}
                        className={`v3-btn ${value === o.value ? 'v3-btn-primary' : ''}`}
                        style={{ fontSize: 11, padding: '4px 10px' }}
                    >
                        {t(o.label)}
                    </button>
                ))}
            </div>
        </div>
    );
}

const selectStyle = {
    padding: '8px 12px',
    background: 'var(--v3-bg-surface)',
    border: '1px solid var(--v3-glass-border)',
    borderRadius: 8,
    color: 'var(--v3-text-primary)',
    fontSize: 13,
    outline: 'none',
};
const controlsRow = { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', marginBottom: 12 };

// ---------- URL state ----------

function parseInitialState(searchParams) {
    const get = (k) => searchParams.get(k);
    const ytd = computePreset('ytd') || {
        from: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
        to: new Date().toISOString().slice(0, 10),
    };

    // New shareable shape
    const enc = get('series');
    if (enc) {
        try {
            const arr = decodeStateParam(enc);
            if (Array.isArray(arr) && arr.length > 0) {
                return {
                    series: arr.slice(0, MAX_SERIES),
                    metric: get('metric') || 'sales',
                    granularity: ['daily', 'monthly', 'yearly'].includes(get('granularity')) ? get('granularity') : 'daily',
                    dateRange: { from: get('from') || ytd.from, to: get('to') || ytd.to },
                };
            }
        } catch (_) { /* fall through to legacy */ }
    }

    // Legacy entityA/entityB shape
    if (get('entityAType') || get('entityBType')) {
        const a = { type: get('entityAType') || 'Category', id: get('entityAId') || '', label: get('entityAId') || '', dateRange: null };
        const b = { type: get('entityBType') || 'Category', id: get('entityBId') || '', label: get('entityBId') || '', dateRange: null };
        return {
            series: [a, b],
            metric: get('metric') || 'sales',
            granularity: ['daily', 'monthly', 'yearly'].includes(get('granularity')) ? get('granularity') : 'daily',
            dateRange: { from: get('from') || ytd.from, to: get('to') || ytd.to },
        };
    }

    // Default: 2 empty rows so the interface still feels familiar
    return {
        series: [EMPTY_ROW(), EMPTY_ROW()],
        metric: 'sales',
        granularity: 'daily',
        dateRange: ytd,
    };
}

function encodeStateParam(arr) {
    try {
        const slim = arr.map(s => ({ type: s.type, id: s.id, label: s.label, dateRange: s.dateRange || null }));
        return btoa(unescape(encodeURIComponent(JSON.stringify(slim))));
    } catch (_) { return ''; }
}

function decodeStateParam(enc) {
    try {
        return JSON.parse(decodeURIComponent(escape(atob(enc))));
    } catch (_) { return null; }
}
